import Markdown from '@/components/atoms/Markdown/Markdown';
import { TContentType } from '@/types/Content.types';
import { Box, Flex, Image, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import ThumbnailPreview from '@/modules/content/common/ThumbnailPreview';
import VideoPlayer from '@/modules/content/common/VideoPlayer';
import { TContentFormValues } from '@/modules/content/creation/model/types';
import { LANDSCAPE_RATIO, probeImageRatio } from '@/modules/content/common/videoOrientation';
import { parseYouTubeId } from '@/modules/content/creation/model/youtube';
import PostPreview from '@/modules/content/creation/steps/PostPreview';

type Props = {
  type: TContentType;
  values: TContentFormValues;
  authorLabel: string;
  thumbnailFile: File | null;
  defaultThumbnailUrl?: string | null;
  isCreatePost?: boolean;
  authorPhotoUrl?: string | null;
};

const PreviewStep = ({
  type,
  values,
  authorLabel,
  thumbnailFile,
  defaultThumbnailUrl,
  isCreatePost = false,
  authorPhotoUrl,
}: Props) => {
  const playbackId = values.videoSource === 'mux' && !values.videoFile ? values.muxPlaybackId : null;
  const [videoAspect, setVideoAspect] = useState<{ playbackId: string; ratio: number } | null>(null);
  const videoRatio = playbackId && videoAspect?.playbackId === playbackId ? videoAspect.ratio : LANDSCAPE_RATIO;

  useEffect(() => {
    if (!playbackId) return;
    let active = true;
    // Resolve the native shape before playback; metadata can refine it below.
    probeImageRatio(`https://image.mux.com/${playbackId}/thumbnail.jpg`).then((ratio) => {
      if (active && ratio) {
        setVideoAspect((current) => (current?.playbackId === playbackId ? current : { playbackId, ratio }));
      }
    });
    return () => {
      active = false;
    };
  }, [playbackId]);

  // Every newly-picked file that needs a displayable URL.
  const files = useMemo(() => {
    const list: File[] = [];
    const add = (file?: File | null) => {
      if (file && !list.includes(file)) list.push(file);
    };
    add(thumbnailFile);
    for (const section of values.sections) {
      add(section.imageFile);
      section.subsections.forEach((sub) => add(sub.imageFile));
    }
    return list;
  }, [thumbnailFile, values.sections]);

  // Object URLs are created in an effect (not useMemo) so they are recreated on every
  // mount — StrictMode's mount→unmount→remount runs the cleanup, and a memoised map
  // would not be rebuilt afterwards, leaving every <img> pointing at a revoked blob.
  const [objectUrls, setObjectUrls] = useState<Map<File, string>>(() => new Map());
  useEffect(() => {
    const map = new Map<File, string>();
    files.forEach((file) => map.set(file, URL.createObjectURL(file)));
    setObjectUrls(map);
    return () => map.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const srcFor = (file?: File | null, url?: string | null) =>
    (file ? objectUrls.get(file) : undefined) || url || undefined;

  if (isCreatePost) {
    return (
      <PostPreview
        title={values.title}
        body={values.description}
        image={srcFor(thumbnailFile, defaultThumbnailUrl)}
        authorName={authorLabel.split(' | ')[0]}
        authorPhotoUrl={authorPhotoUrl}
      />
    );
  }

  return (
    <VStack align="center" width="100%" gap="28px" maxWidth="1040px">
      {/* Two equal-width columns, each centering its item */}
      <Flex width="100%" gap="40px" align="flex-start" justify="center" flexWrap="wrap">
        {/* Left container — thumbnail card (same component as setup, read-only) */}
        <Flex flex={1} minWidth="380px" justify="center">
          <ThumbnailPreview
            type={type}
            title={values.title}
            description={values.description}
            authorLabel={authorLabel}
            domains={values.domains}
            existingUrl={defaultThumbnailUrl}
            file={thumbnailFile}
            onFileChange={() => {}}
            isPublic={values.isPublic}
            readOnly
            postComposer={isCreatePost}
          />
        </Flex>

        {/* Right container — rendered content */}
        <Flex flex={1} minWidth="320px" justify="center">
          <VStack align="stretch" width="100%" maxWidth="480px" gap="16px">
            <Text fontWeight={800} fontSize="22px" color="text.darkBlue">
              {values.title || 'Untitled'}
            </Text>

            {type === 'post' && <Markdown>{values.description || '_No content yet._'}</Markdown>}

            {type === 'video' && (
              <VStack align="stretch" gap="12px">
                <Box width="100%" alignSelf="center">
                  <VideoPlayer
                    key={playbackId ?? values.videoSource}
                    aspectRatio={videoRatio}
                    onRatioDetected={(ratio) => {
                      if (playbackId) setVideoAspect({ playbackId, ratio });
                    }}
                    source={values.videoSource}
                    youtubeVideoId={parseYouTubeId(values.youtubeUrl)}
                    muxStatus={values.muxStatus}
                    muxPlaybackId={values.muxPlaybackId}
                    posterUrl={srcFor(thumbnailFile, defaultThumbnailUrl)}
                    title={values.title}
                    pendingUpload={Boolean(values.videoFile)}
                    emptyHint="Add a valid YouTube link to preview the video."
                  />
                </Box>
                {values.fullDescription && (
                  <Text color="text.darkBlue" fontSize="15px" whiteSpace="pre-wrap">
                    {values.fullDescription}
                  </Text>
                )}
              </VStack>
            )}

            {type === 'article' &&
              values.sections.map((section, index) => {
                const sectionImg = srcFor(section.imageFile, section.imageUrl);
                return (
                  <VStack key={section._id} align="stretch" gap="8px">
                    {section.title && (
                      <Text fontWeight={700} fontSize="18px" color="text.darkBlue">
                        {section.title}
                      </Text>
                    )}
                    <Markdown>{section.text || '_No content yet._'}</Markdown>
                    {sectionImg && (
                      <Image src={sectionImg} alt={section.title || `Section ${index + 1}`} borderRadius="8px" />
                    )}

                    {section.subsections.map((sub) => {
                      const subImg = srcFor(sub.imageFile, sub.imageUrl);
                      return (
                        <VStack key={sub._id} align="stretch" gap="6px" paddingLeft="12px">
                          {sub.title && (
                            <Text fontWeight={700} fontSize="15px" color="text.mediumBlue">
                              {sub.title}
                            </Text>
                          )}
                          <Markdown>{sub.text || '_No content yet._'}</Markdown>
                          {subImg && <Image src={subImg} alt={sub.title || 'Subsection'} borderRadius="8px" />}
                        </VStack>
                      );
                    })}
                  </VStack>
                );
              })}

            {type === 'article' && values.sections.length === 0 && (
              <Text color="text.mediumGray" fontSize="14px">
                No sections yet.
              </Text>
            )}
          </VStack>
        </Flex>
      </Flex>
    </VStack>
  );
};

export default PreviewStep;
